import { type, type Traversal, type Type } from 'arktype';
import { isDateRangeOrdered, rruleSchema } from '@zag/common/validity';
import { dayKeySchema } from '../../models/day-key.model';
import { isWorkdayBitSet } from '../workday-mask';
import { workloadNarrow } from '../workload-narrow';
import type { EmploymentDetails, IsoWeekday } from './employment.model';

interface ShiftWindowFields {
  validFrom: string | null;
  validTo: string | null;
}

interface PeriodDetailsFields {
  workdayMask: number;
  details?: { weekdays: ReadonlyArray<{ weekday: IsoWeekday }> };
}

function isShiftWindowOrdered(shift: ShiftWindowFields, ctx: Traversal): boolean {
  if (shift.validFrom === null || shift.validTo === null) return true;
  return shift.validTo >= shift.validFrom || ctx.mustBe('a validTo on or after validFrom');
}

function isDetailsConsistent(period: PeriodDetailsFields, ctx: Traversal): boolean {
  const weekdays = period.details?.weekdays.map(entry => entry.weekday) ?? [];
  if (!weekdays.every(weekday => isWorkdayBitSet(period.workdayMask, weekday))) {
    return ctx.mustBe('a weekday set in workdayMask');
  }
  return new Set(weekdays).size === weekdays.length || ctx.mustBe('a weekday listed at most once');
}

const isoWeekdaySchema = type('0 | 1 | 2 | 3 | 4 | 5 | 6');

const defaultShiftSchema = type({
  'id?': 'string',
  shiftUnitId: 'string > 0',
  validFrom: dayKeySchema.or('null'),
  validTo: dayKeySchema.or('null'),
  rrule: rruleSchema,
})
  .narrow(isShiftWindowOrdered)
  .onUndeclaredKey('reject');

const weekdayPlanningSchema = type({
  defaultShifts: defaultShiftSchema.array(),
}).onUndeclaredKey('reject');

const weekdayDetailsSchema = type({
  weekday: isoWeekdaySchema,
  'planning?': weekdayPlanningSchema,
}).onUndeclaredKey('reject');

const employmentDetailsSchema = type({
  weekdays: weekdayDetailsSchema.array(),
}).onUndeclaredKey('reject');

interface PeriodWindowFields {
  startDate: string;
  endDate: string | null;
}

function isPeriodWindowOrdered(period: PeriodWindowFields, ctx: Traversal): boolean {
  return isDateRangeOrdered(period.startDate, period.endDate) || ctx.mustBe('an endDate on or after startDate');
}

const employmentPeriodSchema = type({
  'id?': 'string',
  startDate: dayKeySchema,
  endDate: dayKeySchema.or('null'),
  rrule: rruleSchema,
  workdayMask: '0 <= number.integer <= 127',
  'workloadHoursPerDay?': 'number | null',
  'workloadPercent?': 'number | null',
  'balanceAccrualStartDate?': dayKeySchema.or('null'),
  'employmentKey?': 'string | null',
  'details?': employmentDetailsSchema,
})
  .narrow(workloadNarrow)
  .narrow(isDetailsConsistent)
  .narrow(isPeriodWindowOrdered)
  .onUndeclaredKey('reject');

export interface ReplaceEmploymentPeriod {
  id?: string;
  startDate: string;
  endDate: string | null;
  rrule: string | null;
  workdayMask: number;
  workloadHoursPerDay?: number | null;
  workloadPercent?: number | null;
  balanceAccrualStartDate?: string | null;
  employmentKey?: string | null;
  details?: EmploymentDetails;
}

export const replaceEmploymentPeriodsSchema: Type<{ periods: ReplaceEmploymentPeriod[] }> = type({
  periods: employmentPeriodSchema.array(),
}).onUndeclaredKey('reject');
